import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Clock, Send, Pencil, Trash2, Plus, Mail, CheckCircle2, XCircle, MinusCircle, History } from 'lucide-react';
import { fmtDateTimeIST } from '../lib/tz';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Curated timezone list — extend as needed. IANA names required.
const TIMEZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'UTC',
  'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles',
];

const SCOPE_OPTIONS = [
  { value: 'tenant',    label: 'All Portfolios (Tenant)' },
  { value: 'portfolio', label: 'Single Portfolio' },
  { value: 'outlet',    label: 'Single Outlet' },
];

// ── Recipient parsing helpers ────────────────────────────────────────
const parseEmails = (raw) => (raw || '').split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
const stringifyEmails = (arr) => (arr || []).join(', ');

// ── Empty schedule template ──────────────────────────────────────────
const EMPTY = {
  enabled: true, send_time: '09:00', timezone: 'Asia/Kolkata',
  scope_type: 'tenant', scope_id: '',
  recipients: { to: [], cc: [], bcc: [] },
  focus_items: [],
};

const statusPill = (s) => {
  const map = {
    sent:    { bg: '#DEF2E6', fg: '#2D6A4F', icon: CheckCircle2, label: 'Sent' },
    failed:  { bg: '#FDE7E1', fg: '#C45A41', icon: XCircle,     label: 'Failed' },
    skipped: { bg: '#FFF3CD', fg: '#8B6E15', icon: MinusCircle, label: 'Skipped' },
    pending: { bg: '#E2E8DF', fg: '#5C6B64', icon: Clock,       label: 'Pending' },
  }[s] || { bg: '#E2E8DF', fg: '#5C6B64', icon: MinusCircle, label: s || 'unknown' };
  const Icon = map.icon;
  return (
    <span data-testid={`status-pill-${s || 'unknown'}`}
       style={{ background: map.bg, color: map.fg }}
       className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium">
      <Icon className="w-3 h-3" /> {map.label}
    </span>
  );
};

export default function EmailAutomationSection() {
  const { getHeaders } = useAuth();
  const [schedules, setSchedules]     = useState([]);
  const [history, setHistory]         = useState([]);
  const [portfolios, setPortfolios]   = useState([]);
  const [outlets, setOutlets]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [editing, setEditing]         = useState(null);   // schedule doc or null
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [saving, setSaving]           = useState(false);
  const [sendingId, setSendingId]     = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, p, o] = await Promise.all([
        axios.get(`${API}/admin/email/schedules`, { headers: getHeaders() }),
        axios.get(`${API}/admin/email/history?limit=100`, { headers: getHeaders() }),
        axios.get(`${API}/admin/portfolios`,            { headers: getHeaders() }),
        axios.get(`${API}/outlets`,                     { headers: getHeaders() }).catch(() => ({ data: [] })),
      ]);
      setSchedules(s.data?.schedules || []);
      setHistory(h.data?.history || []);
      setPortfolios(Array.isArray(p.data) ? p.data : (p.data?.portfolios || []));
      setOutlets(Array.isArray(o.data) ? o.data : (o.data?.outlets || []));
    } catch (e) {
      toast.error('Failed to load email automation data');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openNew  = () => { setEditing({ ...EMPTY }); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing({
      ...s,
      recipients: {
        to:  stringifyEmails(s.recipients?.to),
        cc:  stringifyEmails(s.recipients?.cc),
        bcc: stringifyEmails(s.recipients?.bcc),
      },
      focus_items_str: (s.focus_items || []).join('\n'),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing) return;
    // Marshal payload — parse recipient strings back to lists.
    const payload = {
      enabled: !!editing.enabled,
      send_time: (editing.send_time || '').trim(),
      timezone: editing.timezone || 'Asia/Kolkata',
      scope_type: editing.scope_type || 'tenant',
      scope_id: editing.scope_id || null,
      recipients: {
        to:  parseEmails(typeof editing.recipients?.to  === 'string' ? editing.recipients.to  : ''),
        cc:  parseEmails(typeof editing.recipients?.cc  === 'string' ? editing.recipients.cc  : ''),
        bcc: parseEmails(typeof editing.recipients?.bcc === 'string' ? editing.recipients.bcc : ''),
      },
      focus_items: (editing.focus_items_str || '').split('\n').map(s => s.trim()).filter(Boolean),
    };
    if (!payload.recipients.to.length) { toast.error('At least one To recipient is required'); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/email/schedules/${editing.id}`, payload, { headers: getHeaders() });
        toast.success('Schedule updated');
      } else {
        await axios.post(`${API}/admin/email/schedules`, payload, { headers: getHeaders() });
        toast.success('Schedule created');
      }
      setDialogOpen(false); setEditing(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete this schedule (${s.send_time} ${s.timezone})?`)) return;
    try {
      await axios.delete(`${API}/admin/email/schedules/${s.id}`, { headers: getHeaders() });
      toast.success('Schedule deleted');
      await loadAll();
    } catch { toast.error('Failed to delete schedule'); }
  };

  const toggleEnabled = async (s) => {
    // Round-trip via full PUT since the API doesn't do partials — same
    // payload shape as save() minus the parsing.
    try {
      await axios.put(`${API}/admin/email/schedules/${s.id}`, {
        enabled: !s.enabled,
        send_time: s.send_time, timezone: s.timezone,
        scope_type: s.scope_type, scope_id: s.scope_id,
        recipients: s.recipients, focus_items: s.focus_items || [],
      }, { headers: getHeaders() });
      await loadAll();
    } catch { toast.error('Failed to update schedule'); }
  };

  const sendNow = async (s) => {
    setSendingId(s.id);
    try {
      const res = await axios.post(`${API}/admin/email/schedules/${s.id}/send-now`,
        {}, { headers: getHeaders() });
      const entry = res.data;
      if (entry?.status === 'sent') {
        toast.success(`Sent (id: ${entry.message_id?.slice(0, 8) || '—'}…)`);
      } else if (entry?.status === 'skipped') {
        toast.warning(`Skipped: ${entry.error}`);
      } else {
        toast.error(`Failed: ${entry?.error || 'unknown'}`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSendingId(null); }
  };

  const scopeLabel = (s) => {
    if (s.scope_type === 'tenant')    return 'All Portfolios';
    if (s.scope_type === 'portfolio') return `Portfolio: ${portfolios.find(p => p.id === s.scope_id)?.name || s.scope_id}`;
    if (s.scope_type === 'outlet')    return `Outlet: ${outlets.find(o => o.id === s.scope_id)?.name    || s.scope_id}`;
    return s.scope_type;
  };

  return (
    <>
      {/* ── Schedules ────────────────────────────────────────────── */}
      <Card className="border-[#E2E8DF] shadow-sm mt-6" data-testid="email-automation-card">
        <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#0E5236]" />
              Daily Business Snapshot — Scheduled Reports
            </CardTitle>
            <Button onClick={openNew} size="sm"
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white h-8 gap-1.5 text-xs"
                    data-testid="add-schedule-btn">
              <Plus className="w-3.5 h-3.5" /> New Schedule
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 py-4">
          <p className="text-sm text-[#5C6B64] mb-3">
            Automatically deliver the Daily Business Snapshot to configured recipients at the chosen local time. Each schedule can be enabled/disabled independently and can target the full tenant, a portfolio, or a single outlet.
          </p>

          {schedules.length === 0 && !loading && (
            <div className="text-center py-8 text-sm text-[#5C6B64] border border-dashed border-[#E2E8DF] rounded-lg"
                 data-testid="schedules-empty">
              No schedules yet. Click <b>New Schedule</b> to configure the first automated Daily Snapshot.
            </div>
          )}

          {schedules.length > 0 && (
            <div className="border border-[#E2E8DF] rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-[#F5F1E8]">
                  <TableRow>
                    <TableHead className="w-[70px] text-xs">Enabled</TableHead>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Scope</TableHead>
                    <TableHead className="text-xs">Recipients</TableHead>
                    <TableHead className="text-xs">Last Run</TableHead>
                    <TableHead className="w-[210px] text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.map(s => (
                    <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                      <TableCell>
                        <Switch checked={!!s.enabled}
                                onCheckedChange={() => toggleEnabled(s)}
                                data-testid={`toggle-${s.id}`} />
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium text-[#1A2421]">{s.send_time}</div>
                        <div className="text-[#5C6B64]">{s.timezone}</div>
                      </TableCell>
                      <TableCell className="text-xs">{scopeLabel(s)}</TableCell>
                      <TableCell className="text-xs">
                        <div><b>To:</b> {(s.recipients?.to || []).join(', ') || '—'}</div>
                        {(s.recipients?.cc?.length || 0) > 0 && (
                          <div className="text-[#5C6B64]"><b>Cc:</b> {s.recipients.cc.join(', ')}</div>
                        )}
                        {(s.recipients?.bcc?.length || 0) > 0 && (
                          <div className="text-[#5C6B64]"><b>Bcc:</b> {s.recipients.bcc.join(', ')}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {s.last_run_at ? (
                          <div className="space-y-1">
                            {statusPill(s.last_run_status)}
                            <div className="text-[10px] text-[#5C6B64]">{fmtDateTimeIST(s.last_run_at)}</div>
                          </div>
                        ) : (
                          <span className="text-[#9CA3AF] text-[11px]">Never</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => sendNow(s)}
                                disabled={sendingId === s.id}
                                className="h-7 text-[11px] gap-1"
                                data-testid={`send-now-${s.id}`}>
                          <Send className="w-3 h-3" /> {sendingId === s.id ? 'Sending…' : 'Send Now'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(s)}
                                className="h-7 text-[11px] px-2" data-testid={`edit-${s.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(s)}
                                className="h-7 text-[11px] px-2 text-[#C45A41] border-[#C45A41]/40 hover:bg-[#FDE7E1]"
                                data-testid={`delete-${s.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Email History ─────────────────────────────────────────── */}
      <Card className="border-[#E2E8DF] shadow-sm mt-6" data-testid="email-history-card">
        <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <History className="w-4 h-4 text-[#0E5236]" />
            Email History — Last 100 Sends
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 py-0">
          {history.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#5C6B64]" data-testid="history-empty">
              No emails have been sent yet.
            </div>
          ) : (
            <div className="overflow-auto max-h-[420px]">
              <Table>
                <TableHeader className="bg-[#F5F1E8] sticky top-0">
                  <TableRow>
                    <TableHead className="text-xs">Sent At</TableHead>
                    <TableHead className="text-xs">Trigger</TableHead>
                    <TableHead className="text-xs">Scope</TableHead>
                    <TableHead className="text-xs">Report Date</TableHead>
                    <TableHead className="text-xs">Recipients</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Message ID / Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                      <TableCell className="text-[11px]">{h.sent_at ? fmtDateTimeIST(h.sent_at) : '—'}</TableCell>
                      <TableCell className="text-[11px]">
                        <Badge variant="outline" className="text-[10px]">
                          {h.trigger}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[11px]">
                        <div className="font-medium">{h.scope_label || h.scope_type}</div>
                      </TableCell>
                      <TableCell className="text-[11px]">{h.target_date}</TableCell>
                      <TableCell className="text-[11px] max-w-[220px] truncate">
                        {(h.recipients?.to || []).join(', ')}
                        {(h.recipients?.cc?.length || 0) > 0 && ` · cc:${h.recipients.cc.length}`}
                        {(h.recipients?.bcc?.length || 0) > 0 && ` · bcc:${h.recipients.bcc.length}`}
                      </TableCell>
                      <TableCell>{statusPill(h.status)}</TableCell>
                      <TableCell className="text-[11px] font-mono max-w-[240px] truncate">
                        {h.message_id || h.error || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Edit / New Dialog ─────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Schedule' : 'New Schedule'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex items-center justify-between border-b border-[#E2E8DF] pb-3">
                <div>
                  <Label className="text-sm">Enable Daily Snapshot</Label>
                  <p className="text-xs text-[#5C6B64]">Turn scheduled delivery on / off without deleting.</p>
                </div>
                <Switch checked={!!editing.enabled}
                        onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                        data-testid="dialog-enabled" />
              </div>

              <div>
                <Label className="text-sm">Send Time (HH:MM, 24-hour)</Label>
                <Input type="time" value={editing.send_time}
                       onChange={e => setEditing({ ...editing, send_time: e.target.value })}
                       className="h-9 mt-1" data-testid="dialog-send-time" />
              </div>
              <div>
                <Label className="text-sm">Time Zone</Label>
                <Select value={editing.timezone}
                        onValueChange={v => setEditing({ ...editing, timezone: v })}>
                  <SelectTrigger className="h-9 mt-1" data-testid="dialog-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Report Scope</Label>
                <Select value={editing.scope_type}
                        onValueChange={v => setEditing({ ...editing, scope_type: v, scope_id: '' })}>
                  <SelectTrigger className="h-9 mt-1" data-testid="dialog-scope-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {editing.scope_type === 'portfolio' && (
                <div>
                  <Label className="text-sm">Portfolio</Label>
                  <Select value={editing.scope_id || ''}
                          onValueChange={v => setEditing({ ...editing, scope_id: v })}>
                    <SelectTrigger className="h-9 mt-1" data-testid="dialog-scope-portfolio">
                      <SelectValue placeholder="Choose portfolio" />
                    </SelectTrigger>
                    <SelectContent>
                      {portfolios.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editing.scope_type === 'outlet' && (
                <div>
                  <Label className="text-sm">Outlet</Label>
                  <Select value={editing.scope_id || ''}
                          onValueChange={v => setEditing({ ...editing, scope_id: v })}>
                    <SelectTrigger className="h-9 mt-1" data-testid="dialog-scope-outlet">
                      <SelectValue placeholder="Choose outlet" />
                    </SelectTrigger>
                    <SelectContent>
                      {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="col-span-2">
                <Label className="text-sm">To (comma or newline separated) <span className="text-[#C45A41]">*</span></Label>
                <Textarea value={typeof editing.recipients?.to === 'string' ? editing.recipients.to
                                    : stringifyEmails(editing.recipients?.to)}
                          onChange={e => setEditing({
                            ...editing,
                            recipients: { ...editing.recipients, to: e.target.value },
                          })}
                          className="min-h-[60px] mt-1 text-sm" data-testid="dialog-to"
                          placeholder="ops@example.com, cx@example.com" />
              </div>
              <div>
                <Label className="text-sm">Cc</Label>
                <Textarea value={typeof editing.recipients?.cc === 'string' ? editing.recipients.cc
                                    : stringifyEmails(editing.recipients?.cc)}
                          onChange={e => setEditing({
                            ...editing,
                            recipients: { ...editing.recipients, cc: e.target.value },
                          })}
                          className="min-h-[54px] mt-1 text-sm" data-testid="dialog-cc" />
              </div>
              <div>
                <Label className="text-sm">Bcc</Label>
                <Textarea value={typeof editing.recipients?.bcc === 'string' ? editing.recipients.bcc
                                    : stringifyEmails(editing.recipients?.bcc)}
                          onChange={e => setEditing({
                            ...editing,
                            recipients: { ...editing.recipients, bcc: e.target.value },
                          })}
                          className="min-h-[54px] mt-1 text-sm" data-testid="dialog-bcc" />
              </div>

              <div className="col-span-2">
                <Label className="text-sm">Today's Focus items (one per line, optional)</Label>
                <Textarea value={editing.focus_items_str || (editing.focus_items || []).join('\n')}
                          onChange={e => setEditing({ ...editing, focus_items_str: e.target.value })}
                          className="min-h-[60px] mt-1 text-sm" data-testid="dialog-focus"
                          placeholder="One bullet per line — appears under Today's Focus in the email." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }}
                    data-testid="dialog-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="dialog-save">
              {saving ? 'Saving…' : (editing?.id ? 'Update Schedule' : 'Create Schedule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
